/* ==========================================================================
   Salon Editorial Commerce — <hws-tabs>
   Generic, reusable tab controller. Progressive enhancement only: without
   this file the first panel stays visible because every other panel carries
   the `hidden` attribute and the CSS `html.no-js` fallback keeps panel one on
   screen.

   Markup contract (ids are up to the section, the values just have to match):

     <hws-tabs data-tabs-activation="auto|manual">
       <div role="tablist" aria-label="…">
         <button type="button" role="tab" data-tab="1"
                 id="Tab-1" aria-controls="Panel-1"
                 aria-selected="true" tabindex="0">…</button>
         …
       </div>
       <div role="tabpanel" data-tab-panel="1" id="Panel-1"
            aria-labelledby="Tab-1" tabindex="0">…</div>
       <div role="tabpanel" data-tab-panel="2" … hidden>…</div>
     </hws-tabs>

   Keyboard: ArrowRight/ArrowDown next, ArrowLeft/ArrowUp previous (both wrap),
   Home first, End last, Enter/Space activate in manual mode.

   Events: fires a bubbling `hws:tabchange` with
   `detail: { value, tab, panel }` on every change, then nudges a window
   resize so lazily measured children (swipe rails, sliders) recalculate now
   that they finally have a width, and re-scans scroll reveals.
   ========================================================================== */

(function () {
  'use strict';
  /* Secties nemen dit bestand elk zelf op, dus staat het soms twee of drie keer
     op één pagina. De browser voert elke tag dan opnieuw uit, waardoor listeners
     op document dubbel hangen en één klik twee acties uitvoert. Deze poort laat
     alleen de eerste uitvoering door. */
  window.__hwsLoaded = window.__hwsLoaded || {};
  if (window.__hwsLoaded['component_tabs']) return;
  window.__hwsLoaded['component_tabs'] = true;


  var TAB = '[data-tab]';
  var PANEL = '[data-tab-panel]';
  var NEXT_KEYS = ['ArrowRight', 'ArrowDown', 'Right', 'Down'];
  var PREV_KEYS = ['ArrowLeft', 'ArrowUp', 'Left', 'Up'];

  /* Descendants that belong to this host and not to a nested <hws-tabs>. */
  function scoped(host, selector) {
    var found = [];
    var nodes = host.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].closest('hws-tabs') === host) found.push(nodes[i]);
    }
    return found;
  }

  class HwsTabs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.tabs = scoped(this, TAB);
      this.panels = scoped(this, PANEL);
      if (!this.tabs.length || !this.panels.length) return;
      this.ready = true;
      this.auto = this.getAttribute('data-tabs-activation') !== 'manual';

      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);

      for (var i = 0; i < this.tabs.length; i++) {
        this.tabs[i].addEventListener('click', this.onClick);
        this.tabs[i].addEventListener('keydown', this.onKeydown);
      }

      this.select(this.initialValue(), { focus: false, notify: false });
    }

    disconnectedCallback() {
      if (!this.tabs) return;
      for (var i = 0; i < this.tabs.length; i++) {
        this.tabs[i].removeEventListener('click', this.onClick);
        this.tabs[i].removeEventListener('keydown', this.onKeydown);
      }
      this.ready = false;
    }

    tabValue(el) {
      return el ? el.getAttribute('data-tab') : null;
    }

    initialValue() {
      for (var i = 0; i < this.tabs.length; i++) {
        if (this.tabs[i].getAttribute('aria-selected') === 'true') {
          return this.tabValue(this.tabs[i]);
        }
      }
      return this.tabValue(this.tabs[0]);
    }

    /* Resolve an incoming value to one that actually exists. */
    resolve(wanted) {
      for (var i = 0; i < this.tabs.length; i++) {
        if (this.tabValue(this.tabs[i]) === wanted) return wanted;
      }
      return this.tabValue(this.tabs[0]);
    }

    currentIndex() {
      for (var i = 0; i < this.tabs.length; i++) {
        if (this.tabs[i].getAttribute('aria-selected') === 'true') return i;
      }
      return 0;
    }

    handleClick(event) {
      event.preventDefault();
      this.select(this.tabValue(event.currentTarget), { focus: true });
    }

    handleKeydown(event) {
      var key = event.key;
      var last = this.tabs.length - 1;
      var index = this.tabs.indexOf(event.currentTarget);
      if (index < 0) index = this.currentIndex();
      var target = -1;

      if (NEXT_KEYS.indexOf(key) > -1) {
        target = index === last ? 0 : index + 1;
      } else if (PREV_KEYS.indexOf(key) > -1) {
        target = index === 0 ? last : index - 1;
      } else if (key === 'Home') {
        target = 0;
      } else if (key === 'End') {
        target = last;
      } else if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        if (this.auto) return;
        event.preventDefault();
        this.select(this.tabValue(this.tabs[index]), { focus: true });
        return;
      } else {
        return;
      }

      var tab = this.tabs[target];
      if (!tab) return;
      event.preventDefault();

      if (this.auto) {
        this.select(this.tabValue(tab), { focus: true });
      } else {
        tab.setAttribute('tabindex', '0');
        tab.focus();
      }
    }

    select(wanted, options) {
      if (!this.tabs || !this.tabs.length) return;
      var opts = options || {};
      var active = this.resolve(wanted);
      var activeTab = null;
      var activePanel = null;
      var i;

      for (i = 0; i < this.tabs.length; i++) {
        var tab = this.tabs[i];
        var on = this.tabValue(tab) === active;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        tab.classList.toggle('is-selected', on);
        if (on) activeTab = tab;
      }

      for (i = 0; i < this.panels.length; i++) {
        var panel = this.panels[i];
        var show = panel.getAttribute('data-tab-panel') === active;
        panel.hidden = !show;
        if (show) activePanel = panel;
      }

      if (activeTab && opts.focus) activeTab.focus();
      if (opts.notify === false) return;

      this.dispatchEvent(
        new CustomEvent('hws:tabchange', {
          bubbles: true,
          detail: { value: active, tab: activeTab, panel: activePanel }
        })
      );

      /* Children measured while hidden (rails, sliders) need a nudge. */
      window.dispatchEvent(new Event('resize'));
      if (window.hwsReveal && typeof window.hwsReveal.scan === 'function') {
        window.hwsReveal.scan();
      }
    }
  }

  if (!customElements.get('hws-tabs')) {
    customElements.define('hws-tabs', HwsTabs);
  }

  /* Tabbladen die later komen (product-rail-tabs: data-rail-laden). Na het laden van de pagina
     in rust ophalen, of meteen als iemand het tabblad kiest. Mislukt het, dan blijft het adres
     staan en probeert de volgende tabwissel het opnieuw. */
  function laadRail(track) {
    if (!track || track.__hwsLaden) return track && track.__hwsLaden;
    var url = track.getAttribute('data-rail-laden');
    if (!url) return null;
    track.__hwsLaden = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelectorAll('li.rail-tabs__item');
        if (!items.length) throw new Error('leeg');
        var frag = document.createDocumentFragment();
        items.forEach(function (li) { frag.appendChild(document.importNode(li, true)); });
        track.appendChild(frag);
        track.removeAttribute('data-rail-laden');
        window.dispatchEvent(new Event('resize'));
        document.dispatchEvent(new CustomEvent('hws:rail:geladen', { detail: { track: track } }));
      })
      .catch(function () { track.__hwsLaden = null; });
    return track.__hwsLaden;
  }
  document.addEventListener('hws:tabchange', function (event) {
    var panel = event.detail && event.detail.panel;
    if (panel) laadRail(panel.querySelector('[data-rail-laden]'));
  });
  function laadAlleRails() {
    document.querySelectorAll('[data-rail-laden]').forEach(laadRail);
  }
  function opRust() {
    if ('requestIdleCallback' in window) window.requestIdleCallback(laadAlleRails, { timeout: 4000 });
    else setTimeout(laadAlleRails, 1500);
  }
  if (document.readyState === 'complete') opRust();
  else window.addEventListener('load', opRust, { once: true });

  /* Theme editor: activate the tab whose block the merchant just selected. */
  document.addEventListener('shopify:block:select', function (event) {
    var node = event.target;
    if (!node || typeof node.querySelector !== 'function') return;
    var tab = node.matches && node.matches(TAB) ? node : node.querySelector(TAB);
    if (!tab) return;
    var host = tab.closest('hws-tabs');
    if (!host || typeof host.select !== 'function') return;
    host.select(tab.getAttribute('data-tab'), { focus: false });
  });
})();
